import { create } from "zustand";
import { persist, PersistStorage, StorageValue } from "zustand/middleware";
import {
  ABVariant,
  EntryPath,
  CurrentView,
  File,
  UserLoginStatus,
  FAQDetailData,
  Market,
  HandoffActionEnum,
} from "@/types";
import { oneDayInMs } from "@/constant";
import { envManager } from "@/config/env";
import { CHAT_API } from "@/config/api";
import { apiGet } from "@/utils/apiHelper";
import type { APIResponse } from "@/utils/apiHelper";
import {
  GLADLY_REFRESH_MESSAGE,
  openGladly,
  shouldPromptGladlyRefresh,
  type ChatModeData,
} from "@/modules/casa/utils/chatMode";
import { toast } from "@/hooks/useToast";
import { throwCasaApiError } from "@/utils/errorHandler";
import { TAILWIND_SM_BREAKPOINT } from "@/lib/tailwindScreens";

const hadHandledProactiveGreetingExpiry = oneDayInMs;
const chatStorageName = "chat-storage";
let viewportResizeHandler: (() => void) | null = null;

const getViewportWidth = () => window.innerWidth;

const getIsMobileViewport = (width = getViewportWidth()) =>
  width < TAILWIND_SM_BREAKPOINT;

interface PersistedProactiveGreeting {
  isHandled: boolean;
  timestamp: number;
}

interface PersistedChatState {
  abVariant: ABVariant;
  chatExpanded?: boolean;
  currentView: CurrentView;
  isLoginInProgress: boolean;
  proactiveGreeting?: PersistedProactiveGreeting;
  lastMessageTimestamp: number | null;
}

interface ChatState {
  // 基础状态
  market: Market | null;
  initMarket: () => void;

  version: string;
  isMobile: boolean;

  serviceAvailable: boolean;
  abVariant: ABVariant;
  entryPath: EntryPath;
  currentView: CurrentView;

  // Chat Expanded
  chatExpanded: boolean;
  isOpeningChat: boolean;
  hadUnreadMessage: boolean;
  lastMessageTimestamp: number | null;

  // User Login Status
  userLoginStatus: UserLoginStatus;
  isLoginInProgress: boolean;

  // Proactive Greeting
  hadHandledProactiveGreeting: boolean;

  // FAQ
  selectedFaq: FAQDetailData | null;

  // Chat
  hasReviewed: boolean;
  isBusinessHours: boolean | null;
  htmlUrl: string | null;
  handoffAction: HandoffActionEnum | null;

  files: File[];

  // Actions
  // Base
  setServiceAvailable: (available: boolean) => void;
  setAbVariant: (variant: ABVariant) => void;
  setEntryPath: (path: EntryPath) => void;
  setCurrentView: (view: CurrentView) => void;
  syncViewport: (width?: number) => void;

  addHadUnReadMessage: () => void;
  removeHadUnReadMessage: () => void;
  updateLastMessageTimestamp: (timestamp: number) => void;

  // Chat State
  openChat: () => Promise<void>;
  closeChat: () => void;

  // User Login Status
  setUserLoginStatus: (status: UserLoginStatus) => void;
  setLoginInProgress: (inProgress: boolean) => void;

  // Proactive Greeting
  userHandledProactiveGreeting: () => void;

  // FAQ
  setSelectedFaq: (faq: FAQDetailData | null) => void;

  setHasReviewed: (hasReviewed: boolean) => void;
  setIsBusinessHours: (isBusinessHours: boolean) => void;
  setHtmlUrl: (url: string | null) => void;
  setHandoffAction: (handoffAction: HandoffActionEnum | null) => void;

  addFile: (file: File) => void;
  updateFile: (file: File) => void;
  clearFiles: () => void;
  delFile: (id: string) => void;

  reset: () => void;
}

const getMarketFromEnv = (): Market | null => {
  const market = envManager.getCurrentMarket();
  if (!market) {
    console.error(
      "[chatStore] Market not found: envManager.getCurrentMarket() returned undefined. Make sure initCasa is called before accessing chatStore.",
    );
    return null;
  }
  const upperMarket = market.toUpperCase() as keyof typeof Market;
  if (upperMarket in Market) {
    return Market[upperMarket];
  }
  console.error(
    `[chatStore] Invalid market value: "${market}" is not a valid Market enum value.`,
  );
  return null;
};

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      // 初始状态
      // 初始状态
      market: null,

      initMarket: () => {
        set({ market: getMarketFromEnv() });
      },

      version: "0.0.1",
      isMobile: getIsMobileViewport(),

      serviceAvailable: true,
      abVariant: "ai",
      entryPath: "faq",
      currentView: "faq",

      chatExpanded: false,
      isOpeningChat: false,
      hadUnreadMessage: false,
      lastMessageTimestamp: null,

      userLoginStatus: "checking",
      isLoginInProgress: false,

      hadHandledProactiveGreeting: false,

      selectedFaq: null,

      conversation: null,
      hasReviewed: false,
      isBusinessHours: null,
      htmlUrl: null,
      handoffAction: null,
      messages: [],

      files: [],

      // Actions
      setServiceAvailable: (available) => set({ serviceAvailable: available }),
      setAbVariant: (variant) => set({ abVariant: variant }),
      setEntryPath: (path) => set({ entryPath: path }),
      setCurrentView: (view) => set({ currentView: view }),
      syncViewport: (width = getViewportWidth()) => {
        const nextIsMobile = getIsMobileViewport(width);
        const { isMobile } = get();

        if (isMobile === nextIsMobile) {
          return;
        }

        set({
          isMobile: nextIsMobile,
          chatExpanded: nextIsMobile ? false : getPersistedChatExpanded(),
        });
      },

      openChat: async () => {
        if (get().isOpeningChat) return;

        set({ isOpeningChat: true });

        try {
          const response = await apiGet<APIResponse<ChatModeData>>(
            CHAT_API.mode(),
          );

          if (response.code === 0 && response.data.mode === "gladly") {
            await openGladly();
            set({ chatExpanded: false, hadHandledProactiveGreeting: true });
            return;
          }

          set({ chatExpanded: true, hadHandledProactiveGreeting: true });
        } catch (error) {
          throwCasaApiError(error, "[Casa api error]: Failed to open chat");
          if (shouldPromptGladlyRefresh(error)) {
            toast({
              description: GLADLY_REFRESH_MESSAGE,
            });
          }
          set({ chatExpanded: true, hadHandledProactiveGreeting: true });
        } finally {
          set({ isOpeningChat: false });
        }
      },
      closeChat: () => set({ chatExpanded: false }),

      updateLastMessageTimestamp: (timestamp: number) => {
        const oldTimestamp = get().lastMessageTimestamp;
        if (oldTimestamp) {
          if (timestamp > oldTimestamp) {
            // 如果不是最新消息，直接返回
            return set({ lastMessageTimestamp: timestamp });
          }
        }
        return set({ lastMessageTimestamp: timestamp });
      },

      setUserLoginStatus: (status) => set({ userLoginStatus: status }),
      setLoginInProgress: (inProgress) =>
        set({ isLoginInProgress: inProgress }),

      userHandledProactiveGreeting: () =>
        set({ hadHandledProactiveGreeting: true }),

      addHadUnReadMessage: () => set({ hadUnreadMessage: true }),

      removeHadUnReadMessage: () => set({ hadUnreadMessage: false }),

      setSelectedFaq: (faq) => set({ selectedFaq: faq }),

      setHtmlUrl: (url) => set({ htmlUrl: url }),
      setHasReviewed: (hasReviewed) => set({ hasReviewed }),
      setIsBusinessHours: (isBusinessHours) => set({ isBusinessHours }),
      setHandoffAction: (handoffAction) => set({ handoffAction }),
      addFile: (file) =>
        set((state) => {
          if (state.files.length >= 3) {
            return { files: state.files };
          }
          return { files: [...state.files, file] };
        }),
      updateFile: (file) =>
        set((state) => ({
          files: state.files.map((f) => (f.id === file.id ? file : f)),
        })),
      clearFiles: () => set({ files: [] }),
      delFile: (id: string) =>
        set((state) => ({
          files: state.files.filter((file) => file.id !== id),
        })),
      reset: () =>
        set({
          // 基础状态
          market: getMarketFromEnv(),

          version: "0.0.1",
          isMobile: getIsMobileViewport(),

          serviceAvailable: true,
          abVariant: "ai",
          entryPath: "faq",
          currentView: "faq",

          // Chat Expanded
          chatExpanded: false,
          isOpeningChat: false,
          hadUnreadMessage: false,
          lastMessageTimestamp: null,

          // User Login Status
          userLoginStatus: "checking",
          isLoginInProgress: false,

          // Proactive Greeting
          hadHandledProactiveGreeting: false,

          // FAQ
          selectedFaq: null,

          // Chat
          hasReviewed: false,
          isBusinessHours: null,
          htmlUrl: null,
          handoffAction: null,

          files: [],
        }),
    }),
    {
      name: chatStorageName,
      storage: createChatStoreStorage(),
      partialize: (state) => {
        const proactiveGreeting = state.hadHandledProactiveGreeting
          ? {
              isHandled: state.hadHandledProactiveGreeting,
              timestamp: Date.now(),
            }
          : undefined;

        return {
          abVariant: state.abVariant,
          ...(state.isMobile
            ? {}
            : {
                chatExpanded: state.chatExpanded,
              }),
          currentView: state.currentView,
          isLoginInProgress: state.isLoginInProgress,
          proactiveGreeting,
          lastMessageTimestamp: state.lastMessageTimestamp,
        };
      },
      merge: (persistedState, currentState) => {
        const prevState: Partial<PersistedChatState> =
          (persistedState as PersistedChatState | undefined) ?? {};
        const hadHandledProactiveGreeting =
          getHadHandledProactiveGreeting(prevState);
        const isMobile = getIsMobileViewport(getViewportWidth());
        const chatExpanded = isMobile
          ? currentState.chatExpanded
          : prevState.chatExpanded ?? currentState.chatExpanded;

        return {
          ...currentState,
          ...prevState,
          isMobile,
          chatExpanded,
          hadHandledProactiveGreeting,
        };
      },
    },
  ),
);

if (typeof window !== "undefined") {
  if (viewportResizeHandler) {
    window.removeEventListener("resize", viewportResizeHandler);
  }

  viewportResizeHandler = () => {
    useChatStore.getState().syncViewport();
  };

  useChatStore.getState().syncViewport();
  window.addEventListener("resize", viewportResizeHandler);
}

function getHadHandledProactiveGreeting(persistedState: any): boolean {
  if (persistedState && persistedState.proactiveGreeting) {
    const { isHandled, timestamp } = persistedState.proactiveGreeting;
    if (Date.now() - timestamp < hadHandledProactiveGreetingExpiry) {
      return isHandled;
    } else {
      return false;
    }
  }

  return false;
}

function createChatStoreStorage():
  | PersistStorage<PersistedChatState>
  | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  return {
    getItem: (name) => readChatStorageValue(name),
    setItem: (name, value) => {
      const previousValue = readChatStorageValue(name);
      const nextValue =
        value.state.chatExpanded === undefined &&
        previousValue?.state.chatExpanded !== undefined
          ? {
              ...value,
              state: {
                ...value.state,
                chatExpanded: previousValue.state.chatExpanded,
              },
            }
          : value;

      window.localStorage.setItem(name, JSON.stringify(nextValue));
    },
    removeItem: (name) => window.localStorage.removeItem(name),
  };
}

function readChatStorageValue(
  name: string
): StorageValue<PersistedChatState> | null {
  if (typeof window === "undefined") {
    return null;
  }

  const item = window.localStorage.getItem(name);

  if (!item) {
    return null;
  }

  try {
    return JSON.parse(item) as StorageValue<PersistedChatState>;
  } catch {
    return null;
  }
}

function getPersistedChatExpanded(): boolean {
  const persistedValue = readChatStorageValue(chatStorageName);

  return persistedValue?.state.chatExpanded ?? false;
}
